import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { Skeleton } from '@mui/material';
import React, { useContext, useState } from 'react';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../shared-interfaces/User/ICoreUser';
import UserService from '../../../../shared-services/User/UserService';
import UserContext from '../../../../shared-components/contexts/UserContext';
import { useSnackbar } from 'notistack';
import { amber } from '@mui/material/colors';

const useStyles = makeStyles(theme => ({
  avatar: {
    height: '60px',
    width: '60px',
    marginRight: '16px',
  },
  row: {
    borderBottom: '1px solid #dcdcdc',
    alignItems: 'center',
  },
  listItemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 'auto',
    marginLeft: 0,
    paddingRight: '8px',
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
    color: '#0066cc',
    [theme.breakpoints.only('xs')]: {
      fontSize: '0.95rem',
    },
  },
  caption: {
    fontSize: '0.85rem',
    color: '#333',
  },
  blockNumbers: {
    textAlign: 'center',
    fontWeight: 600,
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.2rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem',
    },
    [theme.breakpoints.only('xl')]: {
      fontSize: '1.5rem',
    },
  },
  inlineBlock: {
    display: 'inline-block',
    width: '100%',
    fontWeight: 500,
    fontSize: '0.75rem',
    [theme.breakpoints.up('xl')]: {
      fontSize: '0.85rem',
    },
  },
  iconButton: {
    padding: 0,
  },
  listItemIcon: {
    minWidth: '32px',
  },
  following: {
    color: amber[500],
  },
  studentsAlerts: {
    padding: '0 8px',
  },
}));

interface IStudentsListItem {
  coreUser: ICoreUser;
  isFollowing: boolean;
  isCurrentTerm: boolean;
  onFollowChange: (unfollow: boolean) => void;
}

export const StudentsSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem className={classes.row} aria-busy="true" alignItems="flex-start">
      <ListItemIcon className={classes.listItemIcon}>
        <Skeleton variant="circular" height={24} width={24} />
      </ListItemIcon>
      <ListItemAvatar>
        <Skeleton variant="circular" className={classes.avatar} />
      </ListItemAvatar>
      <Box display="flex" flexDirection="row" p={0} style={{ width: '100%' }}>
        <Box p={1} flexGrow={2} className={classes.listItemText}>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="60%" />
        </Box>
        <Box style={{ margin: 'auto' }}>
          <Box alignContent="center" justifyContent="center" display="flex">
            <Skeleton
              variant="text"
              className={classes.blockNumbers}
              width={35}
              height={40}
            />
          </Box>
          <Box alignContent="center" justifyContent="center" display="flex">
            <Skeleton variant="text" width={70} />
          </Box>
        </Box>
        <Box style={{ margin: 'auto' }}>
          <Box alignContent="center" justifyContent="center" display="flex">
            <Skeleton
              variant="text"
              className={classes.blockNumbers}
              width={35}
              height={40}
            />
          </Box>
          <Box alignContent="center" justifyContent="center" display="flex">
            <Skeleton variant="text" width={70} />
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

const StudentsListItem = (props: IStudentsListItem) => {
  const name = GetCoreUserFormattedName(props.coreUser, false);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const user = useContext(UserContext);
  const [following, setFollowing] = useState(props.isFollowing);

  const handleFollow = () => {
    if (following) {
      setFollowing(false);
      UserService.removeFollowing(props.coreUser.id)
        .then(() => props.onFollowChange(true))
        .catch(() => {
          setFollowing(true);
          enqueueSnackbar('Failed to unfollow student, please try again', {
            variant: 'error',
          });
        });
    } else {
      setFollowing(true);
      UserService.createFollowing(user.id, props.coreUser.id)
        .then(() => props.onFollowChange(false))
        .catch(() => {
          setFollowing(false);
          enqueueSnackbar('Failed to follow student, please try again', {
            variant: 'error',
          });
        });
    }
  };

  return (
    <ListItem
      key={props.coreUser.id}
      className={classes.row}
      alignItems="flex-start"
    >
      <ListItemIcon className={classes.listItemIcon}>
        <IconButton
          aria-label="Follow"
          className={classes.iconButton}
          onClick={handleFollow}
          size="large"
        >
          {following ? (
            <Tooltip title="Unfollow">
              <StarIcon className={classes.following} />
            </Tooltip>
          ) : (
            <Tooltip title="Follow">
              <StarBorderIcon />
            </Tooltip>
          )}
        </IconButton>
      </ListItemIcon>
      <ListItemAvatar>
        <Avatar
          alt={name}
          src={
            props.coreUser.profileImageFilePath ??
            props.coreUser.institutionProfileImageFilePath
          }
          className={classes.avatar}
          data-clarity-mask="true"
        />
      </ListItemAvatar>
      <Box display="flex" flexDirection="row" p={0} style={{ width: '100%' }}>
        <Box p={1} flexGrow={1} className={classes.listItemText}>
          <div style={{ width: '100%' }}>
            <Link
              target="_blank"
              href={`/beacon/students/advise/student/${props.coreUser.externalId}`}
              aria-label={`Go to ${name} profile`}
              data-clarity-mask="true"
            >
              <Typography
                variant="h3"
                aria-hidden="false"
                aria-label={name}
                className={classes.name}
                data-clarity-mask="true"
              >
                {name}
              </Typography>
            </Link>
            <Typography
              variant="caption"
              className={classes.caption}
              data-clarity-mask="true"
            >
              {props.coreUser.externalId}
            </Typography>
          </div>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Box p={1} className={classes.studentsAlerts}>
            <Typography align="center" className={classes.blockNumbers}>
              {props.isCurrentTerm
                ? props.coreUser.currentTermOpenAlertCount
                : props.coreUser.alertCount}
            </Typography>
            <Typography
              variant="caption"
              align="center"
              className={classes.inlineBlock}
            >
              Open Alerts
            </Typography>
          </Box>
          <Box p={1} className={classes.studentsAlerts}>
            <Typography align="center" className={classes.blockNumbers}>
              {props.isCurrentTerm
                ? props.coreUser.currentTermAlertCount
                : props.coreUser.allAlertCount}
            </Typography>
            <Typography
              variant="caption"
              align="center"
              className={classes.inlineBlock}
            >
              Total Alerts
            </Typography>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

export default StudentsListItem;
